import { api } from './client';

export type ConsentType = 'PRIVACY' | 'MARKETING';

interface ConsentHistoryItem {
  id: string;
  consentType: ConsentType;
  action: 'GRANTED' | 'REVOKED';
  version: string;
  source: string;
  createdAt: string;
}

interface GrantConsentResponse {
  userId: string;
  consentType: ConsentType;
  action: 'granted';
  version: string;
  bonusAwarded: number;
}

/**
 * Grant user consent (PRIVACY or MARKETING)
 * @returns bonusAwarded - number of bonus points awarded (0 if already granted)
 */
export async function grantConsent(
  userId: string,
  consentType: ConsentType,
  version: string = '1.0'
): Promise<{ bonusAwarded: number }> {
  const response = await api.post<GrantConsentResponse>('/consent/grant', {
    userId,
    consentType,
    version,
  });
  return { bonusAwarded: response.bonusAwarded };
}

/**
 * Revoke user consent
 */
export async function revokeConsent(
  userId: string,
  consentType: ConsentType
): Promise<void> {
  await api.post<{ success: boolean }>('/consent/revoke', {
    userId,
    consentType,
  });
}

/**
 * Get user consent history
 */
export async function getConsentHistory(userId: string): Promise<ConsentHistoryItem[]> {
  return api.get<ConsentHistoryItem[]>(`/consent/history/${userId}`);
}

/** Итог удаления данных (K-025). balanceZeroed — сколько баллов сгорело; welcomeBlocked — баллы тратились,
 * при новой регистрации приветственного бонуса не будет; alreadyErased — данные были удалены раньше. */
export interface DeleteDataResult {
  userId: string;
  balanceZeroed: number;
  welcomeBlocked: boolean;
  alreadyErased: boolean;
}

/**
 * Удаление своих данных (152-ФЗ, ст. 21): карточка обезличивается, баллы сгорают, согласия отзываются, сессия
 * перестаёт действовать. Клиент — только из сессии; userId — свой (сервер сверяет с сессией).
 */
export async function deleteUserAccount(userId: string): Promise<DeleteDataResult> {
  const r = await api.delete<Partial<DeleteDataResult>>(`/consent/${userId}`, { silent: true });
  return {
    userId: r?.userId ?? userId,
    balanceZeroed: r?.balanceZeroed ?? 0,
    welcomeBlocked: r?.welcomeBlocked ?? false,
    alreadyErased: r?.alreadyErased ?? false,
  };
}
